import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useEnhancedAuth } from '@/contexts/AuthContext';
import VerifyEmailBanner from '@/shared/components/VerifyEmailBanner';
import LoadingSpinner from '@/shared/components/LoadingSpinner';

interface PrivateRouteProps {
  children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  // Auth-only gate: decide route access as soon as the auth check resolves. The page
  // behind the gate renders its own skeleton while its user-data loads.
  const { currentUser, isAuthLoading, isStudentViewActive } = useEnhancedAuth();
  const location = useLocation();

  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <LoadingSpinner size="lg" className="text-primary-500" />
      </div>
    );
  }

  // Allow access if user is logged in OR if student view is active (dev mode)
  // But exclude anonymous users from accessing dashboard routes
  if (!currentUser && !isStudentViewActive) {
    const redirectTarget = `${location.pathname}${location.search}`;
    const encoded = encodeURIComponent(redirectTarget);
    return <Navigate to={`/signin?from=${encoded}`} replace />;
  }

  // SECURITY: Block anonymous users - they must sign up properly. /trial-signup
  // never actually read `?from=` (grepped: only TrialSignupPage/TrialSignupFlow
  // consumed this route, and neither looked at it), so retiring the page drops
  // nothing live; route to /try, the current front door into signup.
  if (currentUser?.isAnonymous && !isStudentViewActive) {
    return <Navigate to="/try" replace />;
  }

  // SOFT NUDGE: We no longer hard-gate authenticated routes on email verification.
  // Unverified users get full access and are reminded to verify via the app-wide
  // <VerifyEmailBanner /> rendered below. (The /email-verification-pending route
  // remains reachable, but nothing forces users there anymore.)

  return (
    <>
      <VerifyEmailBanner />
      {children}
    </>
  );
};

export default PrivateRoute;